// react
import { useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Stack, Typography } from '@mui/material';

// shared components
import { darkTheme, lightTheme } from '@/components/highcharts/themes';

import { useTheme } from '@emotion/react';
// * Highcharts
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/highcharts-more';
import 'highcharts/modules/no-data-to-display';

const fmt = (v, digits = 2) =>
  typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : '—';

const fmtPercent = (v, digits = 1) =>
  typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(digits)}%` : '—';

const parsePeriodBoundary = (period, keys) => {
  const rawValue = keys.map((key) => period?.[key]).find(Boolean);
  const timestamp = Date.parse(rawValue);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const toPlotBand = (period, idPrefix, color) => {
  const from = parsePeriodBoundary(period, ['start', 'from', 'period_start']);
  const to = parsePeriodBoundary(period, ['end', 'to', 'period_end']);

  if (from == null || to == null) {
    return null;
  }

  return {
    from: Math.min(from, to),
    to: Math.max(from, to),
    color,
    borderColor: color.replace(/0\.(\d+)\)$/, '0.6)'),
    borderWidth: 1,
    id: `${idPrefix}-${from}-${to}`,
    zIndex: 0,
  };
};

// api
// * RTK Query hooks
import {
  useGetEnergyConsumptionQuery,
  useGetSeuEnergyConsumptionQuery,
} from '@/api/energy-analysis/calculateEnergySavingsApiSlice';

// shared components
import { parseTimestamp } from '@/components/daterange/datePickerUtils';

// components
// * Hooks
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

const BaseloadDeviationChart = ({
  item,
  energyTreeId,
  account,
  alarm,
  defaultFrom,
  defaultTo,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['alarms', 'energy-analysis']);

  console.log('Rendering BaseloadDeviationChart with item:', item);

  // Track series visibility state across item changes
  const [showNormalRange, setShowNormalRange] = useState(false);
  const [showNormalMean, setShowNormalMean] = useState(false);
  const [showConsumption, setShowConsumption] = useState(true);
  const [showAlarmValue, setShowAlarmValue] = useState(false);
  const [showComparisonBands, setShowComparisonBands] = useState(true);
  const [showBaselineBands, setShowBaselineBands] = useState(true);

  const { from, to } = useUrlDateRange({
    zone: 'Europe/Copenhagen',
    toKey: 'electricityAlarmTo',
    fromKey: 'electricityAlarmFrom',
    autoSetDefaults: false,
  });

  const fallbackFrom = parseTimestamp(defaultFrom);
  const fallbackTo = parseTimestamp(defaultTo);
  const effectiveFrom = from || fallbackFrom;
  const effectiveTo = to || fallbackTo;

  const themeOptions = useMemo(
    () => (theme.palette.mode === 'dark' ? darkTheme : lightTheme),
    [theme.palette.mode],
  );

  // Conditionally use the appropriate query based on item type
  const isNode = item?.type === 'node';
  const isSeu = item?.type === 'seu';

  const {
    data: nodeData,
    isFetching: isFetchingNode,
    isLoading: isLoadingNode,
    isError: isErrorNode,
    error: errorNode,
  } = useGetEnergyConsumptionQuery(
    {
      accountId: account?.accountId,
      energyGroupId: energyTreeId,
      nodeId: item?.nodeId,
      start: effectiveFrom?.toISO(),
      end: effectiveTo?.toISO(),
      aggregate: 'hour',
    },
    {
      skip:
        !account?.accountId ||
        !energyTreeId ||
        !item?.nodeId ||
        !alarm ||
        !effectiveFrom ||
        !effectiveTo ||
        !isNode,
    },
  );

  const {
    data: seuData,
    isFetching: isFetchingSeu,
    isLoading: isLoadingSeu,
    isError: isErrorSeu,
    error: errorSeu,
  } = useGetSeuEnergyConsumptionQuery(
    {
      accountId: account?.accountId,
      energyGroupId: energyTreeId,
      seu: item?.seuType,
      start: effectiveFrom?.toISO(),
      end: effectiveTo?.toISO(),
      aggregate: 'hour',
    },
    {
      skip:
        !account?.accountId ||
        !energyTreeId ||
        !item?.seuType ||
        !alarm ||
        !effectiveFrom ||
        !effectiveTo ||
        !isSeu,
    },
  );

  // Use the appropriate data source
  const data = isNode ? nodeData : seuData;
  const isFetching = isNode ? isFetchingNode : isFetchingSeu;
  const isLoading = isNode ? isLoadingNode : isLoadingSeu;
  const isError = isNode ? isErrorNode : isErrorSeu;
  const error = isNode ? errorNode : errorSeu;

  const lower = typeof item?.lower_limit === 'number' ? item.lower_limit : null;
  const upper = typeof item?.upper_limit === 'number' ? item.upper_limit : null;
  const compared =
    typeof item?.compared_value === 'number' ? item.compared_value : null;
  const normalMean =
    lower != null && upper != null ? (lower + upper) / 2 : null;
  const percentageIncrease =
    compared != null && normalMean != null && normalMean !== 0
      ? ((compared - normalMean) / normalMean) * 100
      : null;
  const i18n = useTranslation().i18n;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const seriesLabels = {
    normalRange: t(
      'type.ElectricitySystem.subtype.BaseloadDeviation.series.normal_range',
    ),
    normalMean: t(
      'type.ElectricitySystem.subtype.BaseloadDeviation.series.normal_range_mean',
    ),
    consumption: t(
      'type.ElectricitySystem.subtype.BaseloadDeviation.series.consumption',
    ),
    alarmValue: t(
      'type.ElectricitySystem.subtype.BaseloadDeviation.series.alarm_value',
    ),
    comparisonPeriod: t(
      'type.ElectricitySystem.subtype.BaseloadDeviation.series.comparison_period',
    ),
    baselinePeriod: t(
      'type.ElectricitySystem.subtype.BaseloadDeviation.series.baseline_period',
    ),
  };
  const noDataMessage =
    isLoading || isFetching
      ? t('type.ElectricitySystem.subtype.BaseloadDeviation.loading')
      : t('type.ElectricitySystem.subtype.BaseloadDeviation.no_data');

  const options = useMemo(() => {
    const rows = Array.isArray(data?.EnergyData) ? data?.EnergyData : [];

    // pick the value you want to plot
    const yKey = 'sum'; // or 'sum'

    const consumption = rows
      .map((r) => {
        const x = Date.parse(r?.timestamp); // ms since epoch (or NaN)
        const y = r?.[yKey];
        return Number.isFinite(x) && typeof y === 'number' ? [x, y] : null;
      })
      .filter(Boolean)
      .sort((a, b) => a[0] - b[0]);

    // If no valid points, Highcharts will show "no data"
    const xMin = consumption.length ? consumption[0][0] : null;
    const xMax = consumption.length
      ? consumption[consumption.length - 1][0]
      : null;

    const lower =
      typeof item?.lower_limit === 'number' ? item.lower_limit : null;
    const upper =
      typeof item?.upper_limit === 'number' ? item.upper_limit : null;
    const compared =
      typeof item?.compared_value === 'number' ? item.compared_value : null;

    const limitBand =
      xMin != null && xMax != null && lower != null && upper != null
        ? [
            [xMin, lower, upper],
            [xMax, lower, upper],
          ]
        : [];

    const comparedLine =
      xMin != null && xMax != null && compared != null
        ? [
            [xMin, compared],
            [xMax, compared],
          ]
        : [];

    const meanOfLimits =
      lower != null && upper != null ? (lower + upper) / 2 : null;

    const meanLine =
      xMin != null && xMax != null && meanOfLimits != null
        ? [
            [xMin, meanOfLimits],
            [xMax, meanOfLimits],
          ]
        : [];

    // Convert comparison_periods to plotBands
    const comparisonPlotBandColor =
      theme.palette.mode === 'dark'
        ? 'rgba(255, 165, 0, 0.18)'
        : 'rgba(255, 140, 0, 0.14)';

    const comparisonPlotBands = Array.isArray(item?.comparison_periods)
      ? item.comparison_periods
          .map((period) =>
            toPlotBand(period, 'comparison', comparisonPlotBandColor),
          )
          .filter(Boolean)
      : [];

    // Convert baseline_periods to plotBands
    const baselinePlotBandColor =
      theme.palette.mode === 'dark'
        ? 'rgba(165, 75, 192, 0.18)'
        : 'rgba(165, 75, 192, 0.14)';

    const baselinePlotBands = Array.isArray(item?.baseline_periods)
      ? item.baseline_periods
          .map((period) =>
            toPlotBand(period, 'baseline', baselinePlotBandColor),
          )
          .filter(Boolean)
      : [];

    return {
      chart: {
        height: 500,
        zoomType: 'x',
        zooming: {
          mouseWheel: { enabled: false },
        },
        backgroundColor: 'transparent',
      },
      title: { text: null },
      credits: { enabled: false },
      time: { useUTC: false },
      xAxis: {
        type: 'datetime',
        crosshair: { width: 1, color: themeOptions.xAxis.crosshairColor },
        labels: {
          style: {
            color: themeOptions.xAxis.labels.style.color,
          },
        },
        plotBands: [
          ...(showComparisonBands ? comparisonPlotBands : []),
          ...(showBaselineBands ? baselinePlotBands : []),
        ],
      },
      yAxis: {
        crosshair: { width: 1, color: themeOptions.yAxis.crosshairColor },
        gridLineColor: themeOptions.xAxis.gridLineColor,
        lineColor: themeOptions.xAxis.lineColor,
        labels: {
          style: {
            color: themeOptions.xAxis.labels.style.color,
          },
        },
      },
      tooltip: {
        shared: true,
        xDateFormat: '%Y-%m-%d %H:%M',
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: {
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        formatter: function () {
          const header = `<b>${dateFormatter.format(new Date(this.x))}</b><br/>`;
          const pointsInfo = this.points
            .map((point) => {
              const name = point.series.name;
              const value = point.y != null ? fmt(point.y) : '—';
              return `<span style="color:${point.color}">\u25CF</span> ${name}: <b>${value} kWh</b>`;
            })
            .join('<br/>');
          return header + pointsInfo;
        },
      },
      legend: {
        itemStyle: { color: themeOptions.legend.itemStyle.color },
      },
      plotOptions: {
        series: { marker: { enabled: false }, turboThreshold: 0 },
        arearange: {
          fillOpacity: 0.2,
          lineWidth: 0,
          enableMouseTracking: false,
          states: { hover: { enabled: false } },
        },
      },
      series: [
        {
          type: 'arearange',
          name: seriesLabels.normalRange,
          data: limitBand,
          fillOpacity: 0.1,
          visible: showNormalRange,
          events: {
            legendItemClick: function () {
              setShowNormalRange((prev) => !prev);
              return false;
            },
          },
          // zIndex: 0,
        },
        {
          type: 'line',
          name: seriesLabels.normalMean,
          data: meanLine,
          color: theme.palette.mode === 'dark' ? '#D32F2F' : '#B71C1C', // red
          lineWidth: 2,
          enableMouseTracking: false,
          visible: showNormalMean,
          events: {
            legendItemClick: function () {
              setShowNormalMean((prev) => !prev);
              return false;
            },
          },
        },
        {
          type: 'line',
          name: seriesLabels.consumption,
          data: consumption,
          color: theme.palette.mode === 'dark' ? '#3E80D3' : '#143A78',
          visible: showConsumption,
          events: {
            legendItemClick: function () {
              setShowConsumption((prev) => !prev);
              return false;
            },
          },
          // zIndex: 2,
        },
        {
          type: 'line',
          name: seriesLabels.alarmValue,
          data: comparedLine,
          dashStyle: 'Dash',
          color: theme.palette.mode === 'dark' ? '#0BDA51' : '#0AAA40',
          enableMouseTracking: false,
          visible: showAlarmValue,
          events: {
            legendItemClick: function () {
              setShowAlarmValue((prev) => !prev);
              return false;
            },
          },
          // zIndex: 3,
        },
        // Dummy series for comparison period plotbands
        ...(comparisonPlotBands.length > 0
          ? [
              {
                type: 'line',
                name: seriesLabels.comparisonPeriod,
                data: [],
                color:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 165, 0, 0.6)'
                    : 'rgba(255, 140, 0, 0.8)',
                visible: true,
                showInLegend: true,
                enableMouseTracking: false,
                marker: { enabled: false },
                events: {
                  legendItemClick: function () {
                    setShowComparisonBands((prev) => !prev);
                    return false; // Prevent default toggle behavior
                  },
                },
              },
            ]
          : []),
        ...(baselinePlotBands.length > 0
          ? [
              {
                type: 'line',
                name: seriesLabels.baselinePeriod,
                data: [],
                color:
                  theme.palette.mode === 'dark'
                    ? 'rgba(165, 75, 192, 0.7)'
                    : 'rgba(165, 75, 192, 0.9)',
                visible: true,
                showInLegend: true,
                enableMouseTracking: false,
                marker: { enabled: false },
                events: {
                  legendItemClick: function () {
                    setShowBaselineBands((prev) => !prev);
                    return false;
                  },
                },
              },
            ]
          : []),
      ],
      lang: {
        noData: noDataMessage,
      },
    };
  }, [
    data?.EnergyData,
    item,
    showNormalRange,
    showNormalMean,
    showConsumption,
    showAlarmValue,
    showComparisonBands,
    showBaselineBands,
    themeOptions.xAxis.crosshairColor,
    themeOptions.xAxis.gridLineColor,
    themeOptions.xAxis.lineColor,
    themeOptions.xAxis.labels.style.color,
    themeOptions.yAxis.crosshairColor,
    themeOptions.tooltip.backgroundColor,
    themeOptions.tooltip.borderColor,
    themeOptions.legend.itemStyle.color,
    theme.palette.mode,
    dateFormatter,
    noDataMessage,
    seriesLabels.alarmValue,
    seriesLabels.baselinePeriod,
    seriesLabels.comparisonPeriod,
    seriesLabels.consumption,
    seriesLabels.normalMean,
    seriesLabels.normalRange,
  ]);

  if (!item) {
    return (
      <Stack spacing={1} sx={{ ml: 2, mt: 4 }}>
        <Typography variant="body2" color="textSecondary">
          {t(
            'type.ElectricitySystem.subtype.BaseloadDeviation.empty_selection',
          )}
        </Typography>
      </Stack>
    );
  }

  if (isError) {
    return (
      <div>
        {t('type.ElectricitySystem.subtype.BaseloadDeviation.failed_to_load')}
        <pre style={{ whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(error, null, 2)}
        </pre>
      </div>
    );
  }

  return (
    <Stack spacing={1} sx={{ ml: 2 }}>
      <Stack
        direction="row"
        spacing={4}
        flexWrap="wrap"
        justifyContent={'center'}
        useFlexGap
      >
        {[
          {
            key: 'percentage',
            label: t(
              'type.ElectricitySystem.subtype.BaseloadDeviation.percentage_increase',
            ),
            value: t(
              'type.ElectricitySystem.subtype.BaseloadDeviation.percentage_value',
              { value: fmtPercent(percentageIncrease) },
            ),
          },
          typeof item.monthly_quantity_delta === 'number' && {
            key: 'quantity',
            label: t(
              'portfolio.deepdive_alarms.impact.monthly_quantity_delta',
              {
                ns: 'energy-analysis',
              },
            ),
            value: `${new Intl.NumberFormat('da-DK', {
              maximumFractionDigits: 0,
            }).format(item.monthly_quantity_delta)} kWh`,
          },
          typeof item.monthly_cost === 'number' && {
            key: 'cost',
            label: t('portfolio.deepdive_alarms.impact.monthly_cost', {
              ns: 'energy-analysis',
            }),
            value: new Intl.NumberFormat('da-DK', {
              style: 'currency',
              currency: 'DKK',
              maximumFractionDigits: 0,
            }).format(item.monthly_cost),
          },
        ]
          .filter(Boolean)
          .map((figure) => (
            <Stack
              key={figure.key}
              direction="row"
              spacing={1}
              alignItems="center"
              justifyItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {figure.label}
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color={figure.color}
              >
                {figure.value}
              </Typography>
            </Stack>
          ))}
      </Stack>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Stack>
  );
};

export default BaseloadDeviationChart;
